/**
 * Die Transkripte der vier Radio38-Beiträge, wie sie unter jedem Beitrag zum
 * Aufklappen stehen.
 *
 * **Warum sie auf die Seite gehören:** Eine Suchmaschine kann eine MP3 nicht
 * lesen. Alles, was in den vier Beiträgen gesagt wird — Erstgespräch, sechs
 * Wochen, zwei Damen im Wechsel, Care-Sharing —, ist genau das, wonach
 * Angehörige suchen, und stünde ohne die Texte nirgends auf der Seite. Dazu
 * kommt der einfache Grund: Wer nicht hören kann oder unterwegs keinen Ton hat,
 * bekommt den Inhalt sonst gar nicht.
 *
 * **Herkunft:** Maschinell erstellt (Whisper large-v3, aus den Originalen mit
 * 192 kbps, nicht aus den komprimierten Fassungen der Website) und danach von
 * Hand durchgesehen. Korrigiert wurden:
 *
 * - der Nachname, den das Modell durchgehend verhört hat („Anczak",
 *   „Antschack", „Anschack", „Anschlag" → Antczak, ebenso im Firmennamen);
 * - „die mehr bleibt" → „die mir bleibt" und „kann es uns allen gelegen" →
 *   „kann es uns gelingen" (Teil 1);
 * - „Untertitelung. BR 2018" am Ende von Teil 1 — eine bekannte Fehlleistung
 *   des Modells auf dem Musikausklang, kein gesprochenes Wort;
 * - die zum Schluss von Teil 3 in Halbzeilen zerfallene Passage, wieder zu
 *   Sätzen zusammengesetzt;
 * - **unsicher:** „die müssen alle Romy-Cup lernen" (Teil 3, 2:00) steht hier
 *   als „Rommé" — zum Kartenspiel passt es, sicher belegt ist es nicht. Wer die
 *   Aufnahme zur Hand hat, hört an dieser Stelle bitte noch einmal nach.
 *
 * Die Sprecherzuordnung stammt aus dem Gesprächsverlauf; im Ton nennt sich nur
 * Britta Berger selbst beim Namen. Zeitmarken sind Sekunden ab Dateianfang und
 * im Player anklickbar. Absätze fassen zusammengehörende Segmente zusammen —
 * eine Zeile je Whisper-Segment liest niemand.
 *
 * Bewusst **nicht** über `EditableText` bearbeitbar wie die übrigen Texte: Ein
 * Transkript gibt wieder, was gesendet wurde. Wer es im Bearbeiten-Modus
 * umformulieren kann, hat kein Transkript mehr. Die rohe Maschinenfassung hängt
 * an der Trello-Karte „Miteinander Landing Page".
 */

/** Wer spricht. `null` ist der Sendungsjingle, der niemandem gehört. */
export type Sprecher = 'Moderation' | 'Roman Antczak' | 'Britta Berger';

export interface Absatz {
  /** Startzeit in der Aufnahme in Sekunden — Sprungmarke im Player. */
  zeit: number;
  sprecher: Sprecher | null;
  text: string;
}

const MODERATION: Sprecher = 'Moderation';
const ROMAN: Sprecher = 'Roman Antczak';
const BRITTA: Sprecher = 'Britta Berger';

/** Der Jingle steht am Anfang jedes Teils — vier Mal derselbe Wortlaut. */
const JINGLE =
  'Gesund leben, fit sein, bewusst fühlen und fürsorglich begleiten, wenn es selber nicht mehr geht? Aber wie geht das eigentlich wirklich? Jetzt dranbleiben. Ihr hört Gesundheit Plus, die neue Dauerwerbesendung auf Radio 38.';

const TEIL_1: Absatz[] = [
  { zeit: 2, sprecher: null, text: JINGLE },
  {
    zeit: 17,
    sprecher: MODERATION,
    text: 'Zu Hause ist es doch einfach am schönsten. Zu Hause ist der Ort, an dem ich mich wohlfühle und an dem ich sein möchte. Und das gilt natürlich auch im Alter. Im Alter ist oft die Frage: Bleibe ich zu Hause oder muss ich ins Heim? Ist das Heim denn die einzige Möglichkeit, die mir bleibt?',
  },
  {
    zeit: 33,
    sprecher: MODERATION,
    text: 'Darüber wollen wir heute sprechen — mit jemandem, der sagt: Nein, da gibt es noch andere Möglichkeiten. Wir haben heute Roman Antczak zu Gast. Er ist der Inhaber und auch Betreiber der Seniorenbetreuung Antczak. Und mit ihm wollen wir vor allem darüber sprechen, wie denn eine 24-Stunden-Betreuung eine Alternative sein kann zum Thema Heim. Roman, herzlich willkommen. Schön, dass du da bist.',
  },
  { zeit: 53, sprecher: ROMAN, text: 'Ja, schön, dass ich hier sein kann.' },
  {
    zeit: 55,
    sprecher: MODERATION,
    text: 'Wir haben heute schon einiges gemeinsam besprochen. Dein Thema ist es wirklich zu sagen: Du möchtest den Leuten auch im Alter ermöglichen, daheim zu bleiben — und das funktioniert bei dir, bei euch, durch die 24-Stunden-Betreuung.',
  },
  {
    zeit: 68,
    sprecher: ROMAN,
    text: 'Genau, ich habe es mir ein Stück weit zum Lebensziel erklärt, dass ich ältere Menschen dabei unterstütze, in den eigenen vier Wänden zu bleiben. Irgendwann war der Startschuss da, um ein Unternehmen zu gründen in diesem Bereich, und durch meine polnischen Wurzeln wusste ich: Ich werde dabei auf polnisches Personal zurückgreifen. Und für mich war es schon immer wichtig, dass der Mensch immer im Vordergrund stehen wird.',
  },
  {
    zeit: 90,
    sprecher: MODERATION,
    text: 'Das heißt, wenn ich hier in der Region 38 zu Hause bin und ich habe zum Beispiel Eltern oder andere ältere Verwandte, wo ich merke: Hey, die brauchen Betreuung, die brauchen Unterstützung im Alltag, aber Heim ist ein Thema, das sie auf gar keinen Fall möchten — dann wäre ich bei euch richtig?',
  },
  {
    zeit: 108,
    sprecher: ROMAN,
    text: 'Genau, dann wärst du bei uns genau richtig. Und bei uns fängt alles immer mit einem persönlichen Erstgespräch an.',
  },
  {
    zeit: 114,
    sprecher: MODERATION,
    text: 'Persönliches Erstgespräch heißt, du führst das dann wirklich selbst?',
  },
  {
    zeit: 117,
    sprecher: ROMAN,
    text: 'Das ist richtig. Das möchte ich mir nicht nehmen, dass ich eben unsere potenziellen Kunden persönlich kennenlerne.',
  },
  {
    zeit: 123,
    sprecher: MODERATION,
    text: 'Und das hat wahrscheinlich nicht nur den Hintergrund, dass du die Leute kennenlernen möchtest, sondern auch, weil du schauen musst, wer passt — ja, wo einfach das perfekte Match auch entsteht.',
  },
  {
    zeit: 131,
    sprecher: ROMAN,
    text: 'Genau. Also für mich: Ich kann mir nichts anderes vorstellen, als bei so einer wichtigen, herausfordernden Aufgabe das persönliche Gespräch zu führen. Man muss sich kennenlernen, man muss die Bedürfnisse, die Bedarfe gemeinsam besprechen. Nur dann kann es uns gelingen, eine passende Betreuungskraft am Ende auch zu finden.',
  },
  {
    zeit: 151,
    sprecher: MODERATION,
    text: 'Und du sagst schon „passende Betreuungskraft". Das zeichnet sich also nicht nur dadurch aus, wer die Fähigkeiten hat, die gebraucht werden, sondern es muss auch menschlich matchen.',
  },
  {
    zeit: 160,
    sprecher: ROMAN,
    text: 'Genau, die Chemie muss stimmen. Die Dame kann sehr gutes Deutsch sprechen, sie kann Krankenschwester sein — aber wenn die Chemie nicht stimmt, die Tür geht auf und die Dame steht dann vor der Tür, dann werden wir nicht erfolgreich sein. Dann wird diese Art der Betreuung nicht funktionieren.',
  },
  {
    zeit: 176,
    sprecher: MODERATION,
    text: 'Ja, Betreuung ist ja auch einfach wirklich etwas Menschliches, wo auch die Bedürfnisse und Bedarfe der Menschen im Fokus stehen. Wir werden in dieser Stunde noch einiges mehr über die 24-Stunden-Betreuung hören, und wir werden auch eine Dame treffen, die für ihre Familie diese 24-Stunden-Betreuung sich zu Hilfe geholt hat. Ich freue mich auf jeden Fall, weiter darüber zu hören, welche alternativen Konzepte es im Alter gibt.',
  },
];

const TEIL_2: Absatz[] = [
  { zeit: 2, sprecher: null, text: JINGLE },
  {
    zeit: 17,
    sprecher: MODERATION,
    text: 'Heute dreht sich bei uns bei Gesundheit Plus alles um die 24-Stunden-Betreuung im Alltag. Dafür habe ich Roman Antczak von der Seniorenbetreuung Antczak zu Gast. Und wir wollen uns jetzt mal anschauen, Roman: diese 24-Stunden-Betreuung — wie kann ich mir die denn vorstellen? Da kommt jemand zu mir nach Hause bzw. zu meiner Mutter, Großmutter nach Hause und wohnt dann da?',
  },
  {
    zeit: 37,
    sprecher: ROMAN,
    text: 'Genau so muss man sich das vorstellen. Da zieht tatsächlich eine Dame ein. Wir begleiten die Dame immer zu unseren Familien. Ja, und da steht eine Dame mit dem Koffer und sagt Hallo. Und die zieht dann bei den Zubetreuenden mit ein.',
  },
  {
    zeit: 49,
    sprecher: MODERATION,
    text: 'Wow. Und die wohnt dann da für immer, für einen gewissen Zeitraum?',
  },
  {
    zeit: 53,
    sprecher: ROMAN,
    text: 'Sie wohnt mit den Zubetreuenden unter einem Dach, benötigt dafür ein gemütliches Zimmer und idealerweise ein eigenes Bad, und sie bleibt dann sechs Wochen. Das ist bei uns so die durchschnittliche Betreuungsdauer. Danach kommt eine neue Dame, die ebenfalls sechs Wochen bleibt. Wir versuchen immer, ein Team aus zwei Damen zu formen, und das Team wechselt sich dann aufs Jahr gesehen ab.',
  },
  {
    zeit: 75,
    sprecher: MODERATION,
    text: 'Das heißt also: nicht alle sechs Wochen kommt jemand ganz Neues, sondern die beiden Damen wechseln sich dann im Sechs-Wochen-Takt, und dann ist auch jemand wirklich sechs Wochen am Stück für die zu betreuende Person zu Hause und unterstützt, wo sie nur kann.',
  },
  {
    zeit: 89,
    sprecher: ROMAN,
    text: 'Genau so ist das. Die beiden Damen wechseln sich ab, und es sind auch nur die Damen, wo die Chemie stimmt, wo das sehr, sehr gut harmoniert. Und das ist eben sehr wichtig, dass man das gemeinsam mit der Familie erreicht. Dann gibt es keinen Personalwechsel, man muss sich nicht auf neue Damen einstellen.',
  },
  {
    zeit: 105,
    sprecher: MODERATION,
    text: 'Die Damen sind dann da — und als Verwandte frage ich mich natürlich: Welche Aufgaben nehmen sie mir ab?',
  },
  {
    zeit: 112,
    sprecher: ROMAN,
    text: 'Das Besondere ist, dass die Damen mit im Haus mitwohnen oder in der Wohnung. Das heißt, sie sind schon mal einfach da, und sie helfen insbesondere im Bereich Hauswirtschaft. Das fängt an mit Reinigungsarbeiten bis hin zum Einkaufen und Kochen.',
  },
  {
    zeit: 127,
    sprecher: ROMAN,
    text: 'Das andere Leistungsspektrum ist die sogenannte Grundpflege. Das heißt, die Damen unterstützen, wo es gewünscht ist, auch beim Thema Duschen, Waschen, Hilfe beim Anziehen, Hilfe beim Ausziehen — aber auch aktivierende Betreuung, Gesellschaftsspiele, Spaziergänge.',
  },
  {
    zeit: 143,
    sprecher: MODERATION,
    text: 'Ja, das fand ich so super spannend, als du mir das erzählt hast: dass ihr wirklich eben nicht nur reine Pflichtarbeiten leistet, sondern dass da auch wirklich ein Miteinander entsteht, dass es Gesellschaftsspiele gibt, Unterhaltungen gibt. Und das fördert ja auf jeden Fall die Lebensqualität.',
  },
  {
    zeit: 162,
    sprecher: ROMAN,
    text: 'Genau so ist das. Wir sind immer beim Thema Vertrauen, Menschlichkeit, Harmonie. Wenn man sich gut versteht, dann kann man den Alltag auch ganz wunderbar im Einklang meistern, und ja, dann läuft es harmonisch. So muss es sein.',
  },
  {
    zeit: 178,
    sprecher: MODERATION,
    text: 'Ich finde es ganz spannend, dass wir gleich noch mal aus erster Hand hören, wie es so ist, wenn man mit euch zusammenarbeitet, wenn man eure Betreuung in Anspruch nimmt. Da dürfen auch die Hörerinnen und Hörer einmal sehr gespannt sein. Im nächsten Take haben wir einen Erfahrungsbericht, und ich bin schon sehr gespannt, was wir da hören werden.',
  },
];

const TEIL_3: Absatz[] = [
  { zeit: 2, sprecher: null, text: JINGLE },
  {
    zeit: 16,
    sprecher: MODERATION,
    text: 'Die eigene Verwandtschaft in fremde Hände zu geben, das ist eine Herausforderung. Das sind sehr viele Gefühle, die mitspielen. Und wie man damit umgehen kann und wie da auch eine positive Erfahrung daraus entstehen kann, das werden wir uns in diesem Take einmal anhören. Ich darf eine ganz tolle Gästin begrüßen.',
  },
  {
    zeit: 33,
    sprecher: BRITTA,
    text: 'Ja, hallo, schönen Dank für den Empfang. Mein Name ist Britta Berger, und vor vier Jahren haben wir uns an Herrn Antczak gewandt.',
  },
  {
    zeit: 42,
    sprecher: MODERATION,
    text: 'Genau, vor vier Jahren — du sagst es bereits —, da seid ihr auf Herrn Antczak zugetreten und habt gesagt: Hey, wir brauchen Unterstützung bei der Pflege eurer Mutter. Ihr habt euch für eine 24-Stunden-Betreuung entschieden. Wie ist es so, dein Gefühl am Anfang gewesen?',
  },
  {
    zeit: 57,
    sprecher: BRITTA,
    text: 'Wir waren alle sehr gespannt und sehr aufgeregt. Wie wird es meine Mutter aufnehmen, dass jetzt eine fremde Frau bei ihr einziehen soll? Weil sie war nicht davon überzeugt, dass sie Pflege oder Unterstützung überhaupt benötigt. Und dann war das Erstgespräch, und danach war alles klar. Es war ein sehr empathisches Gespräch. Meine Mutter war sehr in den Bann gezogen.',
  },
  {
    zeit: 84,
    sprecher: MODERATION,
    text: 'Und das ist ja auch spannend, da noch mal zu hören: ein Fall, den sehr viele kennen — dass vielleicht erst mal die Eltern oder Großeltern von der Idee nicht so super begeistert sind. Jetzt, vier Jahre später: Wie ist dein Fazit?',
  },
  {
    zeit: 98,
    sprecher: BRITTA,
    text: 'Sehr, sehr positiv. Wir sind immer informiert worden, wenn es Veränderungen gab — weil in den vier Jahren ist natürlich auch mal ein plötzlicher Wechsel entstanden, weil eine Mitarbeiterin krank war. Also wir sind immer gut im Gespräch gewesen.',
  },
  {
    zeit: 114,
    sprecher: MODERATION,
    text: 'Ja, das sind diese kleinen und großen Gesten, die dann wirklich das Gefühl geben: Es ist eben nicht nur eine Dienstleistung.',
  },
  {
    zeit: 120,
    sprecher: BRITTA,
    text: 'Ganz genau. Meine Mutter fühlt sich gut aufgehoben. Im Alter von 90 sind natürlich sehr wenig Freunde noch vorhanden. Sie könnte auch sehr, sehr einsam sein, wenn keiner tagsüber da ist. Aber so sind die Mitarbeiterinnen da — die müssen alle Rommé lernen. Sie fühlt sich einfach wohl.',
  },
  {
    zeit: 137,
    sprecher: MODERATION,
    text: 'Ja, du sprichst es gerade schon an: Neben der Unterstützung im Alltag ist natürlich auch das Thema Einsamkeit ein ganz wichtiges. Habt ihr denn da einen Effekt gemerkt?',
  },
  {
    zeit: 145,
    sprecher: BRITTA,
    text: 'In jedem Fall. Weil meine Mutter fühlt sich zu Hause wohl, sie ist in ihren eigenen vier Wänden, sie kommuniziert mit den lieben Damen und sie ist aufgehoben. Die kochen wunderbar für meine Mutter. Und meine Mutter hat jetzt eben nicht nur, dass sie ihre Einsamkeit beherrscht, sondern man unterstützt sie durch gesunde Ernährung und dadurch, dass darauf geachtet wird, bei Hitze doch noch mal ein Gläschen mehr zu trinken — damit also auch im gesundheitlichen Bereich.',
  },
  {
    zeit: 181,
    sprecher: MODERATION,
    text: 'Vielen Dank für diesen Einblick. Das Thema Pflege ist für uns alle früher oder später ein wichtiges Thema und wird auf jeden Fall auch in den nächsten Jahren ein herausforderndes Thema sein, das neu gedacht werden muss. Ich bin sehr froh, dass sich da Menschen wie Roman gut mit auseinandersetzen. Und deswegen dürfen wir auch in dieser Stunde noch ein bisschen was dazu hören, wie Pflege auch später aussehen kann.',
  },
];

const TEIL_4: Absatz[] = [
  { zeit: 2, sprecher: null, text: JINGLE },
  {
    zeit: 17,
    sprecher: MODERATION,
    text: 'Wie gerne schieben wir das Thema Alter und Pflege und Wohnen im Alter einfach beiseite, müssen uns aber früher oder später leider doch damit auseinandersetzen. Und gerade in den nächsten Jahren stellt sich die Frage: Wie sollen wir das als Gesellschaft, wie sollen wir das als System alles wuppen? Und dafür braucht es neue Systeme, neue Denkansätze.',
  },
  {
    zeit: 39,
    sprecher: MODERATION,
    text: 'Und Roman, ich bin sehr froh, dass wir jetzt noch mal darüber sprechen können, denn ich weiß, dass ihr auch darüber nachdenkt: Wie kann denn Pflege und Betreuung sich auch in den nächsten Jahren entwickeln?',
  },
  {
    zeit: 49,
    sprecher: ROMAN,
    text: 'Ich habe über die Jahre meiner geschäftlichen Tätigkeit festgestellt, dass die Menschen, wenn sie den Bedarf haben, sich immer eins zu eins sehr gut beraten lassen können — sei es im Internet oder durch Pflegestützpunkte oder eben im Bekanntenkreis. Und die Menschen kommen dann auf uns zu, und wir können sie auch eins zu eins sehr gut beraten.',
  },
  {
    zeit: 67,
    sprecher: ROMAN,
    text: 'Was mir aufgefallen ist: dass keiner in die Nachbarschaft guckt. Und ich bin jemand, der an das Miteinander, an die Gemeinschaft glaubt. Und ich sehe das Potenzial, dass, wenn sich Menschen zusammentun und wir die Bedarfe in einem kleinen räumlichen Bereich einmal gemeinsam ermitteln, sich die Menschen dann die Kosten für die Pflege zu Hause teilen können.',
  },
  {
    zeit: 87,
    sprecher: MODERATION,
    text: 'Wir legen da einfach mal direkt den Finger drauf, auf eine Wunde, die wir auch in dem Bereich sehr gerne ignorieren: Pflege kostet, Pflege wird teuer. Du versuchst quasi, Systeme mitzudenken, wie alle an so einer doch lebensbereichernden Variante teilhaben können. Und das nennst du Care-Sharing.',
  },
  {
    zeit: 109,
    sprecher: ROMAN,
    text: 'Richtig. Sharing heißt ja für mich nichts anderes, wie: Man hat gemeinsame Interessen. Und es geht bei der Pflege zu Hause, es geht für uns alle darum, Pflege bezahlbar zu machen. Und deswegen braucht es Ideen und Konzepte, die insbesondere im Fokus haben, dass die Menschen sich die Pflege zu Hause auch langfristig leisten können.',
  },
  {
    zeit: 127,
    sprecher: MODERATION,
    text: 'Das bedeutet also: Wenn sich so eine Pflegegemeinschaft zum Beispiel gründen würde, dass dann eben die betreuenden Personen von euch sich nicht nur um einen Haushalt kümmern würden, sondern dass unter mehreren Haushalten einfach aufgeteilt wird.',
  },
  {
    zeit: 141,
    sprecher: ROMAN,
    text: 'Genau. Mein Ziel wäre es, dass wir, wenn wir den Bedarf einmal ermittelt haben, sagen können: Jetzt kommen drei Damen, die werden bei jemandem untergebracht, der vielleicht ein Zimmer oder eine kleine Wohnung zur Verfügung hat, und die können von dort aus jeden Tag, sage ich mal, fünf Familien versorgen. So können sich fünf Familien vielleicht zwei Damen am Ende teilen, sodass nicht jeder die Kosten tragen muss, sondern dass man das eben auf mehrere Schultern verteilen kann.',
  },
  {
    zeit: 166,
    sprecher: MODERATION,
    text: 'Ja, wer sowohl dieses Prinzip als auch generell die 24-Stunden-Betreuung als Chance sieht — für Verwandte oder auch für sich selbst, um länger in den eigenen vier Wänden zu bleiben, um länger autonom zu bleiben —, der findet euch auf jeden Fall auch unter der Webadresse miteinander38.de. Da können alle Interessierten sich einmal ein bisschen umschauen, und ansonsten findet man auch ganz viele Informationen über dich, euch und vor allem auch über euer Team auf Instagram.',
  },
  {
    zeit: 197,
    sprecher: MODERATION,
    text: 'Roman, vielen Dank für den Einblick in dieses wichtige Thema und auch dafür, dass ihr dieses Thema neu denkt. Ich werde mich auf jeden Fall jetzt damit weiter auseinandersetzen.',
  },
  {
    zeit: 207,
    sprecher: null,
    text: 'Gesundheit Plus, die neue Dauerwerbesendung auf Radio 38.',
  },
];

/** Je Beitragsnummer die Absätze — dieselbe Nummerierung wie `beitrag-N.mp3`. */
export const TRANSKRIPTE: Record<number, Absatz[]> = {
  1: TEIL_1,
  2: TEIL_2,
  3: TEIL_3,
  4: TEIL_4,
};

/**
 * Das Transkript als Fließtext — für die strukturierten Daten (`AudioObject`,
 * Feld `transcript`), die keine Absatzstruktur kennen.
 */
export const alsFliesstext = (nummer: number): string =>
  (TRANSKRIPTE[nummer] ?? [])
    .map((absatz) => (absatz.sprecher ? `${absatz.sprecher}: ${absatz.text}` : absatz.text))
    .join('\n\n');

export default TRANSKRIPTE;
